/**
 * PayU Raty — lazy widget loader.
 *
 * Performance strategy:
 *  1. Zero work przed LCP. Trigger = statyczny HTML (Liquid-rendered).
 *  2. Preconnect do PayU dopiero przy pierwszej interakcji usera (scroll/pointer/key).
 *  3. widget-mini-installments.js ładowany raz, idempotentnie, on-demand:
 *       - na pierwszy klik w .payu-trigger (intent),
 *       - LUB gdy trigger wejdzie w viewport — ale dopiero przez requestIdleCallback,
 *         żeby nie walczyć o wątek w trakcie render/interaction.
 *  4. openWidget() wywoływane natychmiast po miniInstallment() dla danego triggera.
 *  5. variant:change / cart:update — aktualizuje data-payu-amount i re-mountuje
 *     tylko jeśli widget dla tego triggera był już aktywowany.
 */
(() => {
  if (window.__payuWidgetLoader) return;
  window.__payuWidgetLoader = true;

  const SCRIPT_URL = 'https://static.payu.com/res/v2/widget-mini-installments.js';
  const PRECONNECT_ORIGINS = [
    'https://static.payu.com',
    'https://secure.payu.com',
    'https://credit-widget-config.payu.com',
  ];

  const cfg = window.__payuConfig || {};
  const POS_ID = cfg.posId;
  const KEY = cfg.key;

  if (!POS_ID || !KEY) {
    // Settings nieuzupełnione — nie wywalaj błędu, po prostu nic nie rób.
    return;
  }

  // --- Preconnect on first user intent ---
  let preconnected = false;
  const preconnect = () => {
    if (preconnected) return;
    preconnected = true;
    PRECONNECT_ORIGINS.forEach((href) => {
      if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
      const l = document.createElement('link');
      l.rel = 'preconnect';
      l.href = href;
      l.crossOrigin = '';
      document.head.appendChild(l);
    });
  };
  ['pointermove', 'scroll', 'keydown', 'touchstart'].forEach((ev) => {
    addEventListener(ev, preconnect, { once: true, passive: true, capture: true });
  });

  // --- Idempotent script loader ---
  let scriptPromise = null;
  const loadScript = () => {
    if (scriptPromise) return scriptPromise;
    preconnect();
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
      if (existing && window.OpenPayU?.Installments?.miniInstallment) {
        resolve();
        return;
      }
      const s = existing || document.createElement('script');
      s.addEventListener('load', () => resolve(), { once: true });
      s.addEventListener('error', reject, { once: true });
      if (!existing) {
        s.src = SCRIPT_URL;
        s.async = true;
        document.head.appendChild(s);
      }
    });
    return scriptPromise;
  };

  // --- Mount widget for a trigger element ---
  const mountCache = new WeakMap(); // trigger -> Promise<{openWidget, ...}>

  const mount = async (trigger) => {
    const cached = mountCache.get(trigger);
    if (cached) return cached;

    const amount = parseFloat(trigger.dataset.payuAmount);
    if (!amount || isNaN(amount)) return null;

    const uid = trigger.dataset.payuUid || `t${Math.random().toString(36).slice(2, 8)}`;

    // Hidden mount point — widget renderuje do niego, my tylko korzystamy z openWidget().
    let mountEl = document.getElementById(`payu-mount-${uid}`);
    if (!mountEl) {
      mountEl = document.createElement('div');
      mountEl.id = `payu-mount-${uid}`;
      mountEl.style.display = 'none';
      document.body.appendChild(mountEl);
    }

    const promise = loadScript()
      .then(() => {
        if (!window.OpenPayU?.Installments?.miniInstallment) {
          throw new Error('PayU widget not available on window.OpenPayU.Installments');
        }
        return window.OpenPayU.Installments.miniInstallment('#' + mountEl.id, {
          posId: POS_ID,
          key: KEY,
          creditAmount: amount,
          showLongDescription: true,
        });
      })
      .then((result) => {
        trigger.dataset.payuMounted = '1';
        return result;
      })
      .catch((err) => {
        console.warn('[PayU] mount failed', err);
        mountCache.delete(trigger);
        return null;
      });

    mountCache.set(trigger, promise);
    return promise;
  };

  // --- Click handler: mount + openWidget ---
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-payu-trigger]');
    if (!trigger) return;
    e.preventDefault();

    trigger.classList.add('is-loading');
    mount(trigger).then((result) => {
      trigger.classList.remove('is-loading');
      if (result && typeof result.openWidget === 'function') {
        result.openWidget();
      }
    });
  });

  // --- Optional: warm-mount when trigger enters viewport (idle time only) ---
  // Zostawione wyłączone domyślnie — włączaj tylko jeśli chcesz instant-open.
  // Włączenie: window.__payuWarmMount = true przed załadowaniem loadera.
  if (window.__payuWarmMount && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const trigger = entry.target;
        io.unobserve(trigger);
        const warm = () => mount(trigger);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(warm, { timeout: 3000 });
        } else {
          setTimeout(warm, 1500);
        }
      });
    }, { rootMargin: '200px' });
    const observeAll = () => document.querySelectorAll('[data-payu-trigger]').forEach((t) => io.observe(t));
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeAll, { once: true });
    } else {
      observeAll();
    }
  }

  // --- Refresh amount on variant / cart changes ---
  const refreshTrigger = (trigger, newAmountPln, newInstallments) => {
    if (newAmountPln != null) trigger.dataset.payuAmount = newAmountPln;
    if (newInstallments != null) trigger.dataset.payuInstallments = newInstallments;

    // Jeśli trigger nie był jeszcze zamontowany — nic nie rób, kliknięcie zmontuje z nową kwotą.
    if (!trigger.dataset.payuMounted) return;

    // Był zamontowany — usuń cache i zamontuj ponownie w tle (nie otwieraj).
    mountCache.delete(trigger);
    delete trigger.dataset.payuMounted;
  };

  // PDP variant change — nasłuch na dwa eventy żeby pokryć różne implementacje themu.
  const variantEvents = ['variant:change', 'variant-change', 'on:variant:change'];
  variantEvents.forEach((evName) => {
    document.addEventListener(evName, (e) => {
      const variant = e.detail?.variant || e.detail;
      if (!variant || !variant.price) return;
      const pricePln = variant.price / 100;
      document
        .querySelectorAll('[data-payu-context="product"] [data-payu-trigger]')
        .forEach((t) => {
          // Re-render amount text inline (żeby user widział aktualizację bez otwierania widgetu).
          const installments = parseInt(t.dataset.payuInstallments, 10);
          if (installments > 0) {
            const monthly = (pricePln / installments).toFixed(2).replace('.', ',');
            const amountEl = t.querySelector('.payu-trigger__amount');
            if (amountEl) amountEl.textContent = `${monthly} zł/mies.`;
          }
          refreshTrigger(t, pricePln, null);
        });
    });
  });

  // Cart update — nasłuch na Shopify cart events (różne nazwy w różnych themach).
  const cartEvents = ['cart:update', 'cart:refresh', 'cart:change'];
  cartEvents.forEach((evName) => {
    document.addEventListener(evName, async () => {
      try {
        const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!r.ok) return;
        const cart = await r.json();
        const pricePln = cart.total_price / 100;
        document
          .querySelectorAll('[data-payu-context="cart"] [data-payu-trigger], [data-payu-context="drawer"] [data-payu-trigger]')
          .forEach((t) => refreshTrigger(t, pricePln, null));
      } catch (_) {}
    });
  });
})();
